type RegionCode = 'au' | 'eu';

export interface AiRoutingContext {
  isAuthenticated: boolean;
  tenantName?: string;
  userToken?: string;
}

const SUPPORTED_AI_BASE_DOMAINS = new Set(['devcb.in', 'chargebee.com']);

const CLIENT_REGION_TO_SUBDOMAIN: Record<string, RegionCode | ''> = {
  'ap-southeast-2': 'au',
  'eu-central-1': 'eu',
  'us-east-1': '',
  'us-east-2': '',
};

type JwtPayload = {
  client_region?: string;
};

function base64UrlDecode(value: string): string {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');

  if (typeof atob === 'function') {
    return atob(padded);
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(padded, 'base64').toString('utf-8');
  }

  throw new Error('No base64 decoder available');
}

function decodeJwtPayload(token?: string): JwtPayload | null {
  if (!token) return null;

  const [, payload] = token.split('.');
  if (!payload) return null;

  try {
    const decoded = base64UrlDecode(payload);
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
}

function getAiBaseDomain(hostname: string): string | null {
  const match = hostname.match(/^public\.ai\.(.+)$/);
  if (!match) return null;

  const baseDomain = match[1];
  return SUPPORTED_AI_BASE_DOMAINS.has(baseDomain) ? baseDomain : null;
}

function normalizeApiRoot(url: URL): string {
  const path = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
  return `${url.protocol}//${url.host}${path}`;
}

function getRegionSubdomain(token?: string): RegionCode | '' | null {
  const payload = decodeJwtPayload(token);
  if (!payload?.client_region) return null;

  return CLIENT_REGION_TO_SUBDOMAIN[payload.client_region] ?? null;
}

export function resolveAiApiRoot(
  publicApiRoot: string,
  routingContext?: AiRoutingContext
): string {
  if (!routingContext?.isAuthenticated) {
    return publicApiRoot;
  }

  const tenantName = routingContext.tenantName?.trim();
  if (!tenantName) {
    return publicApiRoot;
  }

  let publicUrl: URL;
  try {
    publicUrl = new URL(publicApiRoot);
  } catch {
    return publicApiRoot;
  }

  const baseDomain = getAiBaseDomain(publicUrl.hostname);
  if (!baseDomain) {
    return publicApiRoot;
  }

  const regionSubdomain = getRegionSubdomain(routingContext.userToken);
  if (regionSubdomain === null) {
    return publicApiRoot;
  }

  const tenantHost = regionSubdomain
    ? `${tenantName}.ai.${regionSubdomain}.${baseDomain}`
    : `${tenantName}.ai.${baseDomain}`;

  const tenantUrl = new URL(publicApiRoot);
  tenantUrl.hostname = tenantHost;
  return normalizeApiRoot(tenantUrl);
}
